/**
 * Závislý výber školenie → firma → stroje → typ lektora → školiteľ.
 * Čistá funkcia: číta iba explicitný snapshot, nie Formik ani React state.
 */

export function normalizePositiveId(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return normalizePositiveId(value.id ?? value.lecturer_id);
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) return null;
  return n;
}

export function trainingIdsEqual(a, b) {
  const left = normalizePositiveId(a);
  const right = normalizePositiveId(b);
  return left != null && right != null && left === right;
}

export function normalizeMachineIds(machineIds) {
  const seen = new Set();
  const out = [];
  for (const item of machineIds || []) {
    const id = normalizePositiveId(item?.id ?? item);
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function findById(list, id) {
  const wanted = normalizePositiveId(id);
  if (wanted == null) return null;
  return (list || []).find((item) => trainingIdsEqual(item?.id, wanted)) || null;
}

function isTrainingModuleContact(contact) {
  if (contact?.module == null || contact.module === '') return true;
  return String(contact.module).toLowerCase() === 'training';
}

function contactMachineIds(contact) {
  return normalizeMachineIds(contact?.machines);
}

function contactCoversAllMachines(contact, selectedMachineIds) {
  if (!selectedMachineIds.length) return true;
  const assigned = new Set(contactMachineIds(contact));
  return selectedMachineIds.every((id) => assigned.has(id));
}

function toMachineOption(id, machines) {
  const found = findById(machines, id);
  if (found) return { id: found.id, name: found.name };
  return { id, name: `Stroj #${id}` };
}

function eligibleContactsForCompany(contacts, companyId, selectedMachineIds) {
  const cid = normalizePositiveId(companyId);
  if (cid == null) return [];
  return (contacts || []).filter((contact) => (
    isTrainingModuleContact(contact)
    && trainingIdsEqual(contact.company_id, cid)
    && contactCoversAllMachines(contact, selectedMachineIds)
  ));
}

function availableMachinesForCompany(contacts, machines, companyId, allowAllWithoutCompany) {
  const cid = normalizePositiveId(companyId);
  const catalog = machines || [];
  if (cid == null) {
    return allowAllWithoutCompany ? catalog.map((m) => ({ id: m.id, name: m.name })) : [];
  }
  const ids = new Set();
  for (const contact of contacts || []) {
    if (!isTrainingModuleContact(contact) || !trainingIdsEqual(contact.company_id, cid)) continue;
    contactMachineIds(contact).forEach((id) => ids.add(id));
  }
  const fromCatalog = catalog
    .filter((m) => ids.has(normalizePositiveId(m?.id)))
    .map((m) => ({ id: m.id, name: m.name }));
  const missing = [...ids]
    .filter((id) => !fromCatalog.some((m) => trainingIdsEqual(m.id, id)))
    .map((id) => toMachineOption(id, catalog));
  return [...fromCatalog, ...missing];
}

function distinctLecturerTypes(contacts, lecturerTypes) {
  const seen = new Set();
  const out = [];
  for (const contact of contacts || []) {
    const id = normalizePositiveId(contact?.lecturer_id);
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    const found = findById(lecturerTypes, id);
    out.push(found ? { id: found.id, name: found.name } : { id });
  }
  return out;
}

function keepOrAutoSelect(currentId, options) {
  if (findById(options, currentId)) return normalizePositiveId(currentId);
  if (options.length === 1) return normalizePositiveId(options[0].id);
  return null;
}

export function resolveTrainingSelectionDefaults(training, previous = {}) {
  const companyId = normalizePositiveId(
    training?.training_company_id ?? training?.company_id ?? previous.companyId
  );
  const fromTraining = normalizeMachineIds(training?.machines);
  return {
    companyId,
    machineIds: fromTraining.length ? fromTraining : normalizeMachineIds(previous.machineIds),
  };
}

export function toTrainingSelectionFormValues(result) {
  return {
    company_id: result?.companyId ?? '',
    machines: result?.selectedMachines ?? [],
    lecturer_id: result?.lecturerTypeId ?? '',
    company_contact_id: result?.companyContactId ?? '',
  };
}

export function constrainAvailableMachines(availableMachines, { allowedMachineIds, contact } = {}) {
  let next = Array.isArray(availableMachines) ? availableMachines : [];
  const allowed = normalizeMachineIds(allowedMachineIds);
  if (allowed.length) {
    const allow = new Set(allowed);
    next = next.filter((m) => allow.has(normalizePositiveId(m?.id)));
  }
  if (contact) {
    const cover = new Set(contactMachineIds(contact));
    next = next.filter((m) => cover.has(normalizePositiveId(m?.id)));
  }
  return next;
}

/**
 * @param {object} snapshot
 * @param {object} [snapshot.selectedTraining]
 * @param {string|number|null} snapshot.companyId
 * @param {Array} snapshot.machineIds
 * @param {string|number|null} snapshot.lecturerTypeId
 * @param {string|number|null} snapshot.companyContactId
 * @param {Array} snapshot.companies
 * @param {Array} snapshot.machines
 * @param {Array} snapshot.lecturerTypes
 * @param {Array} snapshot.companyContacts
 * @param {boolean} [snapshot.includeLecturerCascade]
 * @param {boolean} [snapshot.allowAllMachinesWithoutCompany]
 * @param {Array} [snapshot.allowedMachineIds] training-restricted machine ids; empty/omitted = no training subset
 * @param {boolean} [snapshot.constrainToSelectedContact]
 */
export function computeTrainingSelectionCascade(snapshot = {}) {
  const includeLecturerCascade = snapshot.includeLecturerCascade !== false;
  const allowAllMachinesWithoutCompany = snapshot.allowAllMachinesWithoutCompany === true;
  const companyId = normalizePositiveId(snapshot.companyId);
  const company = findById(snapshot.companies, companyId)
    || (companyId != null ? { id: companyId } : null);

  let availableMachines = availableMachinesForCompany(
    snapshot.companyContacts,
    snapshot.machines,
    companyId,
    allowAllMachinesWithoutCompany
  );
  const selectedContact = snapshot.constrainToSelectedContact
    ? findById(snapshot.companyContacts, snapshot.companyContactId)
    : null;
  availableMachines = constrainAvailableMachines(availableMachines, {
    allowedMachineIds: snapshot.allowedMachineIds,
    contact: selectedContact,
  });
  let selectedMachineIds = normalizeMachineIds(snapshot.machineIds)
    .filter((id) => availableMachines.some((m) => trainingIdsEqual(m.id, id)));
  if (!selectedMachineIds.length && availableMachines.length === 1) {
    selectedMachineIds = [normalizePositiveId(availableMachines[0].id)];
  }
  const selectedMachines = selectedMachineIds.map((id) => toMachineOption(id, availableMachines));

  if (!includeLecturerCascade) {
    return {
      companyId,
      company,
      availableMachines,
      selectedMachineIds,
      selectedMachines,
      availableLecturerTypes: [],
      lecturerTypeId: null,
      availableContacts: [],
      companyContactId: null,
    };
  }

  const eligible = eligibleContactsForCompany(
    snapshot.companyContacts,
    companyId,
    selectedMachineIds
  );
  const availableLecturerTypes = distinctLecturerTypes(eligible, snapshot.lecturerTypes);

  const existingContact = findById(eligible, snapshot.companyContactId);
  let lecturerTypeId = keepOrAutoSelect(snapshot.lecturerTypeId, availableLecturerTypes);
  if (lecturerTypeId == null && existingContact) {
    lecturerTypeId = normalizePositiveId(existingContact.lecturer_id);
  }

  const availableContacts = lecturerTypeId == null
    ? []
    : eligible.filter((contact) => trainingIdsEqual(contact.lecturer_id, lecturerTypeId));
  const companyContactId = keepOrAutoSelect(snapshot.companyContactId, availableContacts);

  return {
    companyId,
    company,
    availableMachines,
    selectedMachineIds,
    selectedMachines,
    availableLecturerTypes,
    lecturerTypeId,
    availableContacts,
    companyContactId,
  };
}

export function computeCompanyMachineCascade(snapshot = {}) {
  return computeTrainingSelectionCascade({
    ...snapshot,
    lecturerTypeId: null,
    companyContactId: null,
    lecturerTypes: [],
    includeLecturerCascade: false,
    allowAllMachinesWithoutCompany: snapshot.allowAllMachinesWithoutCompany !== false,
  });
}
